/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction } from "redux"
import { notification } from "@duxton-web/ui"
import { actions } from "store/duck/bulkUpload/actions"
import { AppState } from "store/store.type"
import { AxiosAction } from "types/api"
import { useTranslation } from "common/utils/i18n"
import {
  ACTION_TYPE,
  ExportMenuItemProperty,
  InventoryCategoryResponse,
  InventoryExportedFileResponse,
} from "types/delv-platform-api/inventoryCategory"
import { BULK_UPLOAD_TYPE } from "pages/menu/detail/bulkUpload/BulkUploadContext"
import {
  useFetchInventoryExportedFileMutation,
  useLazyFetchInventoryCategoryQuery,
} from "queries/bulkUploadApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import { downloadFromSafeUrl } from "utils/misc"
import { TroyWebTracker } from "utils/tracking"

/**
 * Standalone "Download BCRS suggestions" trigger for nudge banners shown
 * outside the bulk-upload drawer (e.g. the menu overview). Exports the whole
 * menu with the BCRS column(s) appended; isForBcrsRecommendation makes the
 * backend return ONLY the pending recommendation rows (eligible, unconfirmed
 * AI rec, no BCRS metadata yet). This banner only renders when enableBCRS is
 * on, so the flag is already gated at the call site.
 */
export const useDownloadBcrsSuggestions = () => {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState(false)
  const exportFailedText = useTranslation(
    "bulkUpload.notifications.exportFileFailed.title",
  )
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)

  const [
    fetchInventoryExportedFileRtk,
  ] = useFetchInventoryExportedFileMutation()
  const [fetchInventoryCategoryRtk] = useLazyFetchInventoryCategoryQuery()
  const {
    flags: {
      fetchInventoryCategory: isRtkFetchInventoryCategoryEnabled,
      fetchInventoryExportedFile: isRtkFetchInventoryExportedFileEnabled,
    },
  } = useRtkQueryMigrationEndpointsFlag()

  /**
   * Reuse the regular menu-export category source instead of deriving IDs from
   * state.menu. For a category-based menu, /v1/menu/categories returns real
   * category IDs. For a department-based (mart) menu, the backend adapts active
   * sub-departments into the same response shape with SectionDepartmentID in
   * categoryID. /v1/items/export then interprets the IDs according to menu type.
   *
   * isErrorReported tells the caller a failure was already surfaced to the user:
   * axiosBaseQuery notifies on RTK errors itself (showError defaults to true),
   * while the legacy redux action stays silent. Without it a failed RTK fetch
   * would stack a second "export failed" toast on top of the generic one.
   */
  const fetchMenuCategoryIDs = async (): Promise<{
    categoryIDs?: string[]
    isErrorReported: boolean
  }> => {
    let response: InventoryCategoryResponse | undefined
    let isErrorReported = false

    if (isRtkFetchInventoryCategoryEnabled) {
      const result = await fetchInventoryCategoryRtk({
        merchantID,
        merchantGroupID,
        categoryName: "",
        menuGroupID,
        bulkUploadType: BULK_UPLOAD_TYPE.MENU,
      })
      if (result.error) {
        isErrorReported = true
      } else {
        response = result.data
      }
    } else {
      const result = (await dispatch(
        actions.fetchInventoryCategory(
          {
            categoryName: "",
            menuGroupID,
          },
          merchantID,
          merchantGroupID,
          BULK_UPLOAD_TYPE.MENU,
        ) as AnyAction,
      )) as AxiosAction<InventoryCategoryResponse>
      if (!result?.error) {
        response = result?.payload?.data
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchInventoryCategory,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isRtkFetchInventoryCategoryEnabled,
        FROM_RTK_QUERY: isRtkFetchInventoryCategoryEnabled,
        FROM_OLD_REDUX: !isRtkFetchInventoryCategoryEnabled,
        ACTION_NAME: "fetchInventoryCategory",
      },
    })

    const categoryIDs = response?.categories
      ?.map(category => category.categoryID)
      .filter((id): id is string => !!id)

    return {
      categoryIDs: categoryIDs?.length ? categoryIDs : undefined,
      isErrorReported,
    }
  }

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      // No isLoading guard: while the migration flags are still resolving they
      // read as their DEFAULT_RTK_QUERY_MIGRATION_ENDPOINT_FLAGS value (false), so a click lands on the
      // legacy transport instead of being silently dropped.
      const { categoryIDs, isErrorReported } = await fetchMenuCategoryIDs()
      if (!categoryIDs) {
        if (!isErrorReported) {
          notification.error({ message: exportFailedText })
        }
        return
      }

      if (isRtkFetchInventoryExportedFileEnabled) {
        // RTK Query path
        const result = await fetchInventoryExportedFileRtk({
          merchantID,
          merchantGroupID,
          bulkUploadType: BULK_UPLOAD_TYPE.MENU,
          categoryIDs,
          menuGroupID,
          properties: Object.values(ExportMenuItemProperty),
          templateType: ACTION_TYPE.UPDATE,
          includeBcrsColumn: true,
          isForBcrsRecommendation: true,
        })

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.fetchInventoryExportedFile,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkFetchInventoryExportedFileEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "fetchInventoryExportedFile",
          },
        })

        if (result.error) {
          notification.error({ message: exportFailedText })
          return
        }

        downloadFromSafeUrl(result.data?.downloadUrl)
      } else {
        // Legacy Redux path
        const res = (await dispatch(
          actions.fetchInventoryExportedFile(
            {
              categoryIDs,
              menuGroupID,
              properties: Object.values(ExportMenuItemProperty),
              templateType: ACTION_TYPE.UPDATE,
              includeBcrsColumn: true,
              isForBcrsRecommendation: true,
            },
            merchantID,
            merchantGroupID,
            BULK_UPLOAD_TYPE.MENU,
          ) as AnyAction,
        )) as AxiosAction<InventoryExportedFileResponse>

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.fetchInventoryExportedFile,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkFetchInventoryExportedFileEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "fetchInventoryExportedFile",
          },
        })

        // redux-observable resolves the promise with res.error = true on a
        // 4xx/5xx rather than throwing, so try/catch won't see it — surface it.
        if (res?.error) {
          notification.error({ message: exportFailedText })
          return
        }

        downloadFromSafeUrl(res?.payload?.data?.downloadUrl)
      }
    } finally {
      setIsDownloading(false)
    }
  }

  return { onDownload, isDownloading }
}
